import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import { ModalPopup, ModalcontentInfo, ModalbuttonWrap, Modalbutton, ModalbuttonNo } from './style';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { toggleRecalculateLayoutModalSliceState } from 'store/slices/RecalculateLayoutSlice';

import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { toggleManualToolDoneButtonState, toggleManualToolModalState, toggleManualToolResetButtonState } from 'store/slices/ManualToolModal';

import useCreateDeleteFacet from 'hooks/api/facet/useCreateDeleteFacet';
import useDeleteObstructionQuery from 'hooks/api/deleteObstruction';
import useTranslatePanelPositionsMutation from 'hooks/api/facet/useTranslateFacet';
import useRemovePanelsQuery from 'hooks/api/panel/removePanel';
import useMovePanelsQuery from 'hooks/api/panel/movePanel';
import useAddPanelsQuery from 'hooks/api/panel/addPanel';
import { setPanelMove, setAddPanel, setDeleteIndividualPanelsMode } from 'store/slices/PanelSlice';
import { setDeletedRoofIndexes, setRoofIndexes,
	resetDeletedObstructionIDs, setUserModificationState, updateTransition } from 'store/slices/ToolSlice';
import { isInHouseSimulationMode as checkIsInHouseSimulationMode } from '../../../../util';
import { FacetEditShadingStatus, latestFacetShadingStatus } from 'api/editDsm';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';
import { useRefs } from 'contexts/RefContext';
import { hasShadingAffectingTransform } from '../util';

export default function RecalculateLayoutModal() {
	const { open, warningMessage } = useSelector(
		(state: RootState) => state.recalculateLayoutModal.data
	);
	const { newlyCreatedFacets, deletedRoofIndexes, roofIndexes, deletedObstructionIDs } = 
	useSelector((state: RootState) => state.toolNewPostions.data);
	const { newlyAddedPanels, deletedPanels, currentMovablePanels } = useSelector((state: RootState) => state.panelSlice.data);
	const { uuid, panel, designType, mode, allRoofSegs } = useSelector((state: RootState) => state.roofData.data);

	const { konvaRef } = useRefs();
	const dispatch = useDispatch<AppDispatch>();
	const startShadingRecalc = useStartShadingRecalc();

	const hasTransformedFacet = !!roofIndexes.length;
	const hasCreatedFacet = !!Object.keys(newlyCreatedFacets).length;
	const hasDeletedFacet = !!deletedRoofIndexes.length;
	const hasDeletedObstruction = !!deletedObstructionIDs.length;
	const isInHouseSimulationMode = checkIsInHouseSimulationMode(mode);

	const hasPanelsAdded = newlyAddedPanels.length > 0;
	const hasPanelsDeleted = deletedPanels.length > 0;
	const hasPanelsMoved = currentMovablePanels.length > 0;

	const createNewFacetsMutation = useCreateDeleteFacet();
	const translatePanelPositionsMutation = useTranslatePanelPositionsMutation();

	const addNewPanelsQuery = useAddPanelsQuery();
	const removePanelsQuery = useRemovePanelsQuery();
	const movePanelsQuery = useMovePanelsQuery();

	const deleteObstructionsQuery = useDeleteObstructionQuery();

	function resetAllMOdifiedState(){
		dispatch(setPanelMove({ shouldEnablePanelMove: false }));
		dispatch(setAddPanel({ shouldEnable: false }));
		dispatch(setDeleteIndividualPanelsMode({ enabled: false }));
	
		dispatch(setDeletedRoofIndexes({ deletedRoofIndexes: [] }));
		dispatch(setRoofIndexes({ roofIndexes: [] }));
			
		dispatch(resetDeletedObstructionIDs());
		dispatch(setUserModificationState({ hasMadeChanges: true }));
		dispatch(updateTransition({ currentTransitionState: false }));

		dispatch(toggleManualToolModalState({ value: false }));
		dispatch(toggleManualToolDoneButtonState({ inputFieldsEdited: false }));
		dispatch(toggleManualToolResetButtonState({ resetEnabled: false }));
	}

	async function handleClick(){
		// snapshot before the mutations: read off the edited konva groups
		const hasShadingAffectingFacetTransform = hasTransformedFacet && !!konvaRef?.current
			&& hasShadingAffectingTransform(konvaRef.current, roofIndexes, allRoofSegs);
		const mutations = [];
		// Facet create/delete/transform resolve with a FacetEditShadingStatus — kept apart so their
		// results can seed the shading recompute below.
		const facetShadingEdits: Promise<FacetEditShadingStatus | undefined>[] = [];

		// facet create/delete must resolve before the other mutations below: a pending delete can
		// invalidate facet/panel ids they reference, so they must not fire while it's in flight.
		const facetCreateDeleteResults = (hasCreatedFacet || hasDeletedFacet)
			? await Promise.allSettled([createNewFacetsMutation.mutateAsync()])
			: [];
		// hasTransformedFacet covers pitch, azimuth, row spacing and starting height edits.
		if(hasTransformedFacet) facetShadingEdits.push(translatePanelPositionsMutation.mutateAsync());

		// Panel add/delete/move resolve with a FacetEditShadingStatus as well — a panel on a flat
		// ground mount is a rack on the DSM, so the API bumps the DSM version for those too. They
		// need no FE-side gate: the API returns a claim only when a rebuild is genuinely owed.
		const panelShadingEdits: Promise<FacetEditShadingStatus | undefined>[] = [];

		if (hasPanelsAdded) panelShadingEdits.push(addNewPanelsQuery.mutateAsync());
		if (hasPanelsDeleted) panelShadingEdits.push(removePanelsQuery.mutateAsync());
		if (hasPanelsMoved) panelShadingEdits.push(movePanelsQuery.mutateAsync());

		if (hasDeletedObstruction) mutations.push(deleteObstructionsQuery.mutateAsync());

		const [facetTransformResults, panelShadingResults] = await Promise.all([
			Promise.allSettled(facetShadingEdits),
			Promise.allSettled(panelShadingEdits),
			Promise.allSettled(mutations),
		]);
		const facetShadingResults = [...facetCreateDeleteResults, ...facetTransformResults];

		// Shading is only simulated in-house, so a recompute is meaningless in the other modes. A facet
		// create/delete or a pitch/azimuth change (not a plain move) changes the flux the shading is baked from.
		const shouldRecalcShading = isInHouseSimulationMode && (hasCreatedFacet || hasShadingAffectingFacetTransform || hasDeletedFacet);

		await dispatch(changePanel({ uuid, panelKey: panel.key, designType: designType }));
		resetAllMOdifiedState();
		// One recompute for the batch — keep the highest dsmVersion seen across the gated facet
		// responses and the self-gating panel ones. Undefined (nothing owed) is a no-op.
		await startShadingRecalc(latestFacetShadingStatus([
			...(shouldRecalcShading ? facetShadingResults : []),
			...panelShadingResults,
		]));

	}

	function hanldeConfirmation() {
		closeModal();
		handleClick();
	}

	const closeModal = () => {
		dispatch(toggleManualToolModalState({ value: true, showAll: true }));
		dispatch(toggleRecalculateLayoutModalSliceState({
			open: false,
			warningMessage: '',
		}));
	};

	return (
		<Popup
			contentStyle={{ width: '560px', borderRadius: '4px', margin: '120px auto auto', 
				background: 'var(--modalBgColor)', borderColor: 'var(--borderColor)',
			}}
			open={open}
			closeOnDocumentClick={false}
			onClose={() => {
				if (open) closeModal();
			}}
		>
			<ModalPopup>
				<ModalcontentInfo>
					{warningMessage}
				</ModalcontentInfo>
				<ModalbuttonWrap>
					<Modalbutton onClick={() => hanldeConfirmation()}>
											Yes
					</Modalbutton>
					<ModalbuttonNo onClick={() => closeModal()}>
											No
					</ModalbuttonNo>
				</ModalbuttonWrap>
			</ModalPopup>
		</Popup>
	);
}
